<script setup>
import IconPaintPalette from '@manseImages/yongsin-light/icon-paint-palette.png';
import IconNavigation from '@manseImages/yongsin-light/icon-navigation.png';
import IconNumbers from '@manseImages/yongsin-light/icon-numbers.png';
import IconAlarmClock from '@manseImages/yongsin-light/icon-alarm-clock.png';
import IconOakTree from '@manseImages/yongsin-light/icon-oak-tree.png';
import IconFire from '@manseImages/yongsin-light/icon-fire.png';
import IconMountain from '@manseImages/yongsin-light/icon-mountain.png';
import IconBattle from '@manseImages/yongsin-light/icon-battle.png';
import IconWater from '@manseImages/yongsin-light/icon-water.png';

const props = defineProps({
  contentData: {
    type: Object,
    default: null,
  },
});

/** 오행 → 아이콘 */
const ELEMENT_ICON_MAP = {
  '목': IconOakTree,
  '화': IconFire,
  '토': IconMountain,
  '금': IconBattle,
  '수': IconWater,
};

/** 그리드 카드 아이콘 */
const GRID_ICON_MAP = {
  color: IconPaintPalette,
  direction: IconNavigation,
  number: IconNumbers,
  time: IconAlarmClock,
};

const yongsin = computed(() => props.contentData?.yongsin ?? null);
const heesin = computed(() => props.contentData?.heesin ?? null);
const grid = computed(() => props.contentData?.grid ?? []);

const yongsinIcon = computed(() => ELEMENT_ICON_MAP[yongsin.value?.element] ?? IconOakTree);
const heesinIcon = computed(() => ELEMENT_ICON_MAP[heesin.value?.element] ?? IconMountain);
</script>

<template>
  <div v-if="contentData" class="qu">
    <!-- 용신 요약 카드 -->
    <div
      v-if="yongsin"
      class="qu-summary"
      :style="{
        '--s-color': yongsin.color,
        '--s-bg': yongsin.color + '1A',
        '--s-badge-bg': yongsin.color + '12',
      }"
    >
      <div class="qu-summary__top">
        <div class="qu-summary__left">
          <img :src="yongsinIcon" alt="" class="qu-summary__icon" />
          <div class="qu-summary__info">
            <p class="qu-summary__label">{{ yongsin.label }}</p>
            <p class="qu-summary__name">{{ yongsin.name }}</p>
          </div>
        </div>
        <div class="qu-summary__badges">
          <span
            class="qu-summary__badge"
          >{{ yongsin.keyword }}</span>
        </div>
      </div>
      <div v-if="yongsin.description" class="qu-summary__desc">
        <p v-for="(line, i) in yongsin.description.split('\n')" :key="i">{{ line }}</p>
      </div>
    </div>

    <!-- 희신 요약 카드 -->
    <div
      v-if="heesin"
      class="qu-summary"
      :style="{
        '--s-color': heesin.color,
        '--s-bg': heesin.color + '1A',
        '--s-badge-bg': heesin.color + '12',
      }"
    >
      <div class="qu-summary__top">
        <div class="qu-summary__left">
          <img :src="heesinIcon" alt="" class="qu-summary__icon" />
          <div class="qu-summary__info">
            <p class="qu-summary__label">{{ heesin.label }}</p>
            <p class="qu-summary__name">{{ heesin.name }}</p>
          </div>
        </div>
        <div class="qu-summary__badges">
          <span
            class="qu-summary__badge"
          >{{ heesin.keyword }}</span>
        </div>
      </div>
      <div v-if="heesin.description" class="qu-summary__desc">
        <p v-for="(line, i) in heesin.description.split('\n')" :key="i">{{ line }}</p>
      </div>
    </div>

    <!-- 활동요약 구분선 -->
    <div class="qu-divider">
      <span class="qu-divider__line" />
      <span class="qu-divider__text">활동요약</span>
      <span class="qu-divider__line" />
    </div>

    <!-- 2x2 그리드 -->
    <div class="qu-grid">
      <div v-for="card in grid" :key="card.theme" class="qu-grid__card">
        <div class="qu-grid__icon-area">
          <img :src="GRID_ICON_MAP[card.theme]" alt="" class="qu-grid__icon" />
          <p class="qu-grid__label">{{ card.label }}</p>
        </div>
        <div class="qu-grid__values">
          <p class="qu-grid__main">{{ card.main }}</p>
          <p v-if="card.sub" class="qu-grid__sub">{{ card.sub }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.qu {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ═══ 용신/희신 요약 카드 ═══ */
.qu-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 16px;
  border-radius: 12px;
  background: var(--s-bg);
  border: 1px solid var(--s-color);

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  &__label {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: var(--s-color);
  }

  &__name {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
    color: #333;
  }

  &__badges {
    display: flex;
    gap: 8px;
  }

  &__badge {
    padding: 3px 6px;
    border-radius: 4px;
    background: var(--s-badge-bg);
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    color: var(--s-color);
    white-space: nowrap;
  }

  &__desc {
    background: #fff;
    border-radius: 6px;
    padding: 12px 16px;

    p {
      margin: 0;
      font-size: 15px;
      font-weight: 400;
      line-height: 1.4;
      color: #333;
    }
  }
}

/* ═══ 활동요약 구분선 ═══ */
.qu-divider {
  display: flex;
  align-items: center;
  gap: 12px;

  &__line {
    flex: 1;
    height: 1px;
    background: #d9d9d9;
  }

  &__text {
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: #575757;
    white-space: nowrap;
  }
}

/* ═══ 2x2 그리드 ═══ */
.qu-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  &__card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 20px 16px;
    border-radius: 12px;
    border: 1px solid #ebebeb;
    height: 150px;
  }

  &__icon-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
  }

  &__icon {
    width: 45px;
    height: 45px;
    object-fit: contain;
  }

  &__label {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    color: #333;
  }

  &__values {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__main {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.4;
    color: #333;
    text-align: center;
  }

  &__sub {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    color: #757575;
    text-align: center;
  }
}
</style>
